import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import ItemList from '../../common/utils/ItemList';
const noActiveExtensionErrorMessage = 'You must select an active extension via `.for()` before using extensionData.';
export default class AdminRegistry {
  constructor() {
    _defineProperty(this, "state", {
      currentExtension: null,
      data: {}
    });
  }
  /**
   * This function simply takes the extension id
   *
   * @example
   * app.registry.for('flarum-tags')
   *
   * flarum/flags -> flarum-flags | acme/extension -> acme-extension
   */
  for(extension) {
    this.state.currentExtension = extension;
    this.state.data[extension] = this.state.data[extension] || {};
    return this;
  }

  /**
   * This function registers your settings with Flarum
   *
   * It takes either a settings object or a callback.
   *
   * @example
   *
   * .registerSetting({
   *   setting: 'flarum-flags.guidelines_url',
   *   type: 'text', // This will be inputted into the input tag for the setting (text/number/etc)
   *   label: app.translator.trans('flarum-flags.admin.settings.guidelines_url_label')
   * }, 15) // priority is optional (ItemList)
   */
  registerSetting(content, priority, key) {
    if (priority === void 0) {
      priority = 0;
    }
    if (key === void 0) {
      key = null;
    }
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const tmpContent = content;

    // Callbacks can be passed in instead of settings to display custom content.
    // By default, they will be added with the `null` key, since they don't have a `.setting` attr.
    // To support multiple such items for one extension, we assign a random ID.
    // 36 is arbitrary length, but makes collisions very unlikely.
    if (tmpContent instanceof Function) {
      tmpContent.setting = key || Math.random().toString(36);
    }
    const settings = this.state.data[this.state.currentExtension].settings || new ItemList();
    settings.add(tmpContent.setting, tmpContent, priority);
    this.state.data[this.state.currentExtension].settings = settings;
    return this;
  }

  /**
   * This function allows you to change the configuration of a setting.
   */
  setSetting(key, content) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const settings = this.state.data[this.state.currentExtension].settings || new ItemList();
    if (settings.has(key)) {
      if (content instanceof Function) {
        const original = settings.get(key);
        content = content(original);
      }
      settings.setContent(key, content);
    }
    return this;
  }

  /**
   * This function allows you to change the priority of a setting.
   */
  setSettingPriority(key, priority) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const settings = this.state.data[this.state.currentExtension].settings || new ItemList();
    if (settings.has(key)) {
      settings.setPriority(key, priority);
    }
    return this;
  }

  /**
   * This function allows you to remove a setting.
   */
  removeSetting(key) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const settings = this.state.data[this.state.currentExtension].settings || new ItemList();
    if (settings.has(key)) {
      settings.remove(key);
    }
    return this;
  }

  /**
   * This function registers your permission with Flarum
   *
   * @example
   *
   * .registerPermission('permissions', {
   *     icon: 'fas fa-flag',
   *     label: app.translator.trans('flarum-flags.admin.permissions.view_flags_label'),
   *     permission: 'discussion.viewFlags'
   * }, 'moderate', 65)
   */
  registerPermission(content, permissionType, priority) {
    if (priority === void 0) {
      priority = 0;
    }
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const permissions = this.state.data[this.state.currentExtension].permissions || {};
    const permissionsForType = permissions[permissionType] || new ItemList();
    if (!content.permission && !content.id) {
      throw new Error('Permission definition must have either a permission or id attribute.');
    }
    permissionsForType.add(content.permission || content.id, content, priority);
    this.state.data[this.state.currentExtension].permissions = _objectSpread(_objectSpread({}, permissions), {}, {
      [permissionType]: permissionsForType
    });
    return this;
  }

  /**
   * This function allows you to change the configuration of a permission.
   */
  setPermission(key, content, permissionType) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const permissions = this.state.data[this.state.currentExtension].permissions || {};
    const permissionsForType = permissions[permissionType] || new ItemList();
    if (permissionsForType.has(key)) {
      if (content instanceof Function) {
        const original = permissionsForType.get(key);
        content = content(original);
      }
      permissionsForType.setContent(key, content);
    }
    return this;
  }

  /**
   * This function allows you to change the priority of a permission.
   */
  setPermissionPriority(key, permissionType, priority) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const permissions = this.state.data[this.state.currentExtension].permissions;
    const permissionsForType = (permissions == null ? void 0 : permissions[permissionType]) || new ItemList();
    if (permissionsForType.has(key)) {
      permissionsForType.setPriority(key, priority);
    }
    return this;
  }

  /**
   * This function allows you to remove a permission.
   */
  removePermission(key, permissionType) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    const permissions = this.state.data[this.state.currentExtension].permissions;
    const permissionsForType = (permissions == null ? void 0 : permissions[permissionType]) || new ItemList();
    if (permissionsForType.has(key)) {
      permissionsForType.remove(key);
    }
    return this;
  }

  /**
   * Replace the default extension page with a custom component.
   * This component would typically extend ExtensionPage
   */
  registerPage(component) {
    if (this.state.currentExtension === null) {
      throw new Error(noActiveExtensionErrorMessage);
    }
    this.state.data[this.state.currentExtension].page = component;
    return this;
  }

  /**
   * Get an extension's registered settings
   */
  getSettings(extensionId) {
    var _this$state$data$exte;
    return (_this$state$data$exte = this.state.data[extensionId]) == null || (_this$state$data$exte = _this$state$data$exte.settings) == null ? void 0 : _this$state$data$exte.toArray();
  }

  /**
   * Get an ItemList of all extensions' registered permissions
   */
  getAllPermissions(type) {
    const items = new ItemList();
    Object.keys(this.state.data).map(extension => {
      var _this$state$data$exte2;
      const extPerms = (_this$state$data$exte2 = this.state.data[extension]) == null || (_this$state$data$exte2 = _this$state$data$exte2.permissions) == null ? void 0 : _this$state$data$exte2[type];
      if (this.extensionHasPermissions(extension) && extPerms !== undefined) {
        items.merge(extPerms);
      }
    });
    return items;
  }

  /**
   * Get a singular extension's registered permissions
   */
  getExtensionPermissions(extension, type) {
    var _this$state$data$exte3;
    const extPerms = (_this$state$data$exte3 = this.state.data[extension]) == null || (_this$state$data$exte3 = _this$state$data$exte3.permissions) == null ? void 0 : _this$state$data$exte3[type];
    if (this.extensionHasPermissions(extension) && extPerms != null) {
      return extPerms;
    }
    return new ItemList();
  }

  /**
   * Checks whether a given extension has registered permissions.
   */
  extensionHasPermissions(extension) {
    var _this$state$data$exte4;
    return ((_this$state$data$exte4 = this.state.data[extension]) == null ? void 0 : _this$state$data$exte4.permissions) !== undefined;
  }

  /**
   * Returns an extension's custom page component if it exists.
   */
  getPage(extension) {
    var _this$state$data$exte5;
    return (_this$state$data$exte5 = this.state.data[extension]) == null ? void 0 : _this$state$data$exte5.page;
  }
  getData() {
    return this.state.data;
  }
}
flarum.reg.add('core', 'admin/utils/AdminRegistry', AdminRegistry);